import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { apiService } from "../../services/api";
import { dictionaries } from "../../utils/dictionary";
import { UserData } from "../../types";
import { theme } from "../../styles/theme";
import { applyRTL } from "../../utils/rtl";
import { setCookie } from "../../utils/cookies";
import logo from "../../assets/images/logo.png";

const COOKIE_DOMAIN = '.mafiaworldtour.org'; // Legacy system compatibility

interface LoginProps {
  onLogin: (userData: UserData) => void;
}

interface LanguageOption {
  value: number;
  label: string;
  short: string;
  flag: string;
}

const LoginBackground = styled.div`
  min-height: 100vh;
  padding: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      circle at 20% 20%,
      rgba(124, 93, 255, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(255, 196, 0, 0.08) 0%,
      transparent 50%
    ),
    #0f0b1d;
  color: ${theme.colors.text.primary};

  &::before,
  &::after {
    content: "";
    position: fixed;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    filter: blur(80px);
    z-index: 0;
    pointer-events: none;
  }

  &::before {
    top: -20%;
    left: -15%;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(124, 93, 255, 0.2),
      transparent 70%
    );
  }

  &::after {
    bottom: -25%;
    right: -10%;
    background: radial-gradient(
      circle at 70% 70%,
      rgba(255, 196, 0, 0.12),
      transparent 65%
    );
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;

    &::before,
    &::after {
      width: 300px;
      height: 300px;
    }
  }
`;

const LoginCard = styled.div`
  position: relative;
  z-index: 1;
  width: min(100%, 420px);
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border-radius: ${theme.borderRadius.xxl};
  border: 1px solid ${theme.colors.border.primary};
  padding: 2.5rem 2rem;
  box-shadow: 0 28px 90px rgba(4, 2, 14, 0.65),
    0 0 0 1px rgba(124, 93, 255, 0.08) inset;
  backdrop-filter: blur(20px);

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    padding: 2rem 1.5rem;
  }
`;

const LoginHeader = styled.header`
  text-align: center;
  margin-bottom: 1.75rem;
`;

const LoginIcon = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 1rem;
  background: linear-gradient(
    135deg,
    rgba(124, 93, 255, 0.2),
    rgba(124, 93, 255, 0.08)
  );
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(124, 93, 255, 0.15);
  padding: 0.4rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const LoginEyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${theme.colors.text.muted};
  margin-bottom: 0.5rem;
`;

const LoginTitle = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin: 0 0 0.35rem;
  color: ${theme.colors.text.primary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
`;

const LoginSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.95rem;
  margin: 0;
`;

const ErrorAlert = styled.div`
  border-radius: ${theme.borderRadius.md};
  background: rgba(255, 77, 109, 0.18);
  border: 1px solid rgba(255, 77, 109, 0.3);
  color: #ffb3c1;
  padding: 0.85rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
`;

const LoginForm = styled.form`
  display: grid;
  gap: 1.25rem;
`;

const FormField = styled.div`
  display: grid;
  gap: 0.5rem;
  text-align: left;
`;

const FormLabel = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  font-size: 0.95rem;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const FormActions = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.95rem 1.5rem;
  border-radius: ${theme.borderRadius.lg};
  border: none;
  background: linear-gradient(135deg, #7c5dff 0%, #5b3fd9 100%);
  color: ${theme.colors.text.primary};
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 24px rgba(124, 93, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  will-change: transform;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(124, 93, 255, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${theme.colors.text.primary};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LanguageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const LanguageLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.colors.text.muted};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LanguageChips = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.4rem;
  }
`;

const LanguageChip = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 80px;
  max-width: 120px;
  padding: 0.6rem 0.8rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(124, 93, 255, 0.5)" : theme.colors.border.secondary};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(124, 93, 255, 0.25), rgba(124, 93, 255, 0.15))"
      : "rgba(255, 255, 255, 0.05)"};
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  will-change: transform;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(124, 93, 255, 0.4);
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(135deg, rgba(124, 93, 255, 0.3), rgba(124, 93, 255, 0.2))"
        : "rgba(255, 255, 255, 0.08)"};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    min-width: 70px;
    max-width: 100px;
    padding: 0.55rem 0.6rem;
    font-size: 0.8rem;
    gap: 0.3rem;
  }
`;

const LoginMeta = styled.p`
  text-align: center;
  color: ${theme.colors.text.muted};
  font-size: 0.85rem;
  margin: 1.5rem 0 0;
  line-height: 1.5;
`;

/**
 * Detect initial language based on localStorage or system locale
 */
const detectInitialLanguage = (): number => {
  // First check localStorage
  const savedLanguage = localStorage.getItem('l');
  if (savedLanguage !== null) {
    const langValue = parseInt(savedLanguage, 10);
    if (!isNaN(langValue) && langValue >= 0 && langValue <= 4) {
      return langValue;
    }
  }

  // Check system language (navigator.language or navigator.languages[0])
  const systemLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
  
  // Parse language after dash: en-ru → ru, uk-uk → uk
  const parts = systemLang.toLowerCase().split('-');
  const langCode = parts.length > 1 ? parts[1] : parts[0];
  
  // Map to our language IDs
  // 0: English (en)
  // 1: Russian (ru)
  // 2: Ukrainian (uk)
  // 3: Georgian (ka)
  // 4: Hebrew (he)
  const languageMap: Record<string, number> = {
    'ru': 1,
    'uk': 2,
    'ua': 2, // Alternative for Ukrainian
    'ka': 3,
    'ge': 3, // Alternative for Georgian
    'he': 4,
    'iw': 4, // Alternative for Hebrew
  };

  return languageMap[langCode] || 0; // Default to English
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState(detectInitialLanguage);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Apply RTL when language changes (including initial mount)
  useEffect(() => {
    applyRTL(language);
  }, [language]);

  // Safely get dictionary with fallback to English
  const dict = (dictionaries[language] || dictionaries[0]).ph;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 3 || password.length > 100) {
      setError(dict.pwlength);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiService.login(email, password);

      if (response.success) {
        const userData = response.success;
        apiService.setToken(userData.token); // This also saves token to cookies

        // Store user data in both localStorage and cookies (for legacy compatibility)
        localStorage.setItem("myName", userData.nickname);
        localStorage.setItem("myAvatar", userData.avatar);
        localStorage.setItem("myID", userData.id.toString());
        localStorage.setItem("l", language.toString());

        // Save to cookies (same as legacy system)
        setCookie("myName", userData.nickname, 30, '/', COOKIE_DOMAIN);
        setCookie("myAvatar", userData.avatar, 30, '/', COOKIE_DOMAIN);
        setCookie("myID", userData.id.toString(), 30, '/', COOKIE_DOMAIN);
        setCookie("l", language.toString(), 30, '/', COOKIE_DOMAIN);

        console.log('[Login] User data saved to localStorage and cookies');

        onLogin(userData);
      } else {
        setError(response.error || dict.login_failed);
      }
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || dict.network_error);
    } finally {
      setLoading(false);
    }
  };

  const languageOptions: LanguageOption[] = [
    { value: 0, label: dict.lang_english, short: "EN", flag: "🇬🇧" },
    { value: 1, label: dict.lang_russian, short: "RU", flag: "🏳️" },
    { value: 2, label: dict.lang_ukrainian, short: "UA", flag: "🇺🇦" },
    { value: 3, label: dict.lang_georgian, short: "GE", flag: "🇬🇪" },
    { value: 4, label: dict.lang_hebrew, short: "HE", flag: "🇮🇱" },
  ];

  const title = dict.header?.trim?.() || dict.header;

  return (
    <LoginBackground>
      <LoginCard>
        <LoginHeader>
          <LoginIcon aria-hidden="true">
            <img src={logo} alt="Mafia World Tour" />
          </LoginIcon>
          <LoginEyebrow>{dict.login}</LoginEyebrow>
          <LoginTitle>{title}</LoginTitle>
          <LoginSubtitle>{dict.welcome_subtitle}</LoginSubtitle>
        </LoginHeader>

        {error && <ErrorAlert role="alert">{error}</ErrorAlert>}

        <LoginForm onSubmit={handleSubmit}>
          <FormField>
            <FormLabel htmlFor="email">{dict.email_label}</FormLabel>
            <FormInput
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={dict.email_placeholder}
              autoComplete="email"
              required
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="password">{dict.pwd}</FormLabel>
            <FormInput
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={dict.password_placeholder}
              autoComplete="current-password"
              required
            />
          </FormField>

          <FormActions>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner role="status" aria-hidden="true" />
                  <span>{dict.loading}...</span>
                </>
              ) : (
                dict.submit
              )}
            </SubmitButton>

            <LanguageSection>
              <LanguageLabel>{dict.choose_language}</LanguageLabel>
              <LanguageChips>
                {languageOptions.map((option) => (
                  <LanguageChip
                    key={option.value}
                    type="button"
                    $active={language === option.value}
                    onClick={() => setLanguage(option.value)}
                    aria-pressed={language === option.value}
                  >
                    <span aria-hidden="true">{option.flag}</span>
                    <span>{option.short}</span>
                  </LanguageChip>
                ))}
              </LanguageChips>
            </LanguageSection>
          </FormActions>
        </LoginForm>

        <LoginMeta>{dict.login_meta}</LoginMeta>
      </LoginCard>
    </LoginBackground>
  );
};

export default Login;
